import { UserSessionResponse } from "src/gen/schema/models/user";
import { ReST } from "src/utils/api/rest/rest";

/**
 * Adding a controller to abort logout events
 * that may come during the tenant switch.
 * API calls can fail while the page is redirecting.
 * This is a single use kill switch.
 */
let abortController: AbortController = new AbortController();
const getSessionAbortController = (): AbortController => {
  if (abortController.signal.aborted) {
    /* once triggered the aborted flag is set and we need a new instance. */
    abortController = new AbortController();
  }
  return abortController;
};

export interface UserSessionWithServerTime extends UserSessionResponse {
  serverTime: Date | undefined;
  serverClockSkewMs: number;
}

/**
 * Extracts the server time from the Date header of the response.
 * @param response
 */
const getServerTime = (response: Response): Date | undefined => {
  const dateHeader = response.headers.get("Date");
  if (dateHeader) {
    const serverTime = new Date(dateHeader);
    return Number.isNaN(serverTime.getTime()) ? undefined : serverTime;
  }
  return undefined;
};

/**
 * Fetches the current user session and attaches server time information to the response.
 * Server time is used to calculate clock skew and ensure accurate session expiration/refresh handling.
 *
 * NOTE:
 * Invoking ReST.get directly is an exception to the general pattern of using generated API functions in src/gen/rest.
 * This is because we need access to the raw Response object to extract server time from headers,
 * which is not possible with the current generated API functions that return parsed JSON directly.
 */
const getUserSessionWithServerTime = (
  options?: RequestInit | undefined
): Promise<UserSessionWithServerTime> => {
  return ReST.get(`/ui/v1/sessions`, options).then((response) =>
    response.json().then((json) => {
      // Extract server time from the response headers to calculate clock skew
      const serverTime = getServerTime(response);

      // Combine the original session response with server time information
      return {
        ...UserSessionResponse.fromJSON(json),
        serverTime,
        serverClockSkewMs: serverTime ? Date.now() - serverTime.getTime() : 0
      };
    })
  );
};

/**
 * Returns the current server time based on the user's session information.
 * This accounts for any clock skew between the client and server to ensure accurate time calculations.
 * If the session does not have server time information, it falls back to the client's current time.
 * @param serverClockSkewMs
 */
const getServerTimeNow = (serverClockSkewMs?: number): Date => {
  return new Date(Date.now() - (serverClockSkewMs ?? 0));
};

export {
  getServerTimeNow,
  getSessionAbortController,
  getUserSessionWithServerTime
};
